import { RiskQuestionnaire } from "@/types/kycReview";

export interface RiskQuestion {
  label: string;
  category: string;
  prefix: string;
  options: string[];
}

// 9 questions, each scored 1/2/3 by column. Ported verbatim from the legacy
// wizard so the questionnaire booleans submitted stay backend-compatible.
export const riskQuestions: RiskQuestion[] = [
  {
    label: "Which of the following instruments have you invested in?",
    category: "instrumentsInvested",
    prefix: "instrumentsInvested",
    options: [
      "Time deposits, money market funds",
      "Government securities, corporate bonds, bond funds",
      "Direct stocks, equity funds, derivatives",
    ],
  },
  {
    label: "What's your priority in investing?",
    category: "investmentPriority",
    prefix: "investmentPriority",
    options: [
      "Capital preservation",
      "Income generation with capital protection",
      "Capital appreciation",
    ],
  },
  {
    label: "How familiar are you with different types of investments?",
    category: "investmentFamiliarity",
    prefix: "investmentFamiliarity",
    options: ["Limited", "Average", "Extensive"],
  },
  {
    label: "Your current investments are mostly in which financial instrument?",
    category: "investmentType",
    prefix: "investmentType",
    options: [
      "Time deposits, money market funds",
      "Government securities, corporate bonds, bond funds",
      "Direct stocks, equity funds, derivatives",
    ],
  },
  {
    label: "How stable are your finances?",
    category: "incomeStability",
    prefix: "incomeStability",
    options: [
      "Somewhat stable",
      "Stable but expecting major expenses in 3 years",
      "Very stable for the next 3 years",
    ],
  },
  {
    label: "How many years can you invest without redeeming your funds?",
    category: "periodWithoutRedemption",
    prefix: "periodWithoutRedemption",
    options: ["1 year", "3 to 5 years", "More than 5 years"],
  },
  {
    label: "How many years until your retirement?",
    category: "yearsUntilRetirement",
    prefix: "yearsUntilRetirement",
    options: ["1 to 7 years", "8 to 15 years", "More than 15 years"],
  },
  {
    label: "How much risk are you willing to take?",
    category: "willingRisk",
    prefix: "willingRisk",
    options: ["Minimal risk", "Moderate risk", "High risk"],
  },
  {
    label: "What portfolio are you most comfortable with?",
    category: "portfolioComfort",
    prefix: "portfolioComfort",
    options: [
      "Highest return of 10%, biggest loss of 5%",
      "Highest return of 40%, biggest loss of 30%",
      "Highest return of 60%, biggest loss of 50%",
    ],
  },
];

export interface RiskProfileDetail {
  label: string;
  description: string;
  recommendedFunds: string[];
}

export const riskProfileDetails: Record<string, RiskProfileDetail> = {
  conservative: {
    label: "Conservative",
    description:
      "You prefer capital protection and modest returns through lower-risk investment funds.",
    recommendedFunds: ["Bond funds", "Money market funds"],
  },
  moderate: {
    label: "Moderate",
    description:
      "You prefer a balance between capital growth and protection through a mix of fixed income and equity exposure.",
    recommendedFunds: ["Balanced funds"],
  },
  aggressive: {
    label: "Aggressive",
    description:
      "You are comfortable with market movement and can stay invested for longer-term growth potential.",
    recommendedFunds: [
      "Stock or equity funds",
      "Index funds",
      "Alpha funds",
      "Strategic growth funds",
    ],
  },
};

export function selectedRiskOption(
  questionnaire: RiskQuestionnaire,
  question: RiskQuestion
): number | "" {
  const answer =
    (questionnaire?.[question.category] as Record<string, unknown>) || {};
  if (answer[`${question.prefix}1`]) return 1;
  if (answer[`${question.prefix}2`]) return 2;
  if (answer[`${question.prefix}3`]) return 3;
  return "";
}

export function setRiskAnswer(
  questionnaire: RiskQuestionnaire,
  question: RiskQuestion,
  selected: number
): RiskQuestionnaire {
  return {
    ...questionnaire,
    [question.category]: {
      [`${question.prefix}1`]: selected === 1,
      [`${question.prefix}2`]: selected === 2,
      [`${question.prefix}3`]: selected === 3,
    },
  };
}

export function computeRiskScore(questionnaire: RiskQuestionnaire): number {
  return riskQuestions.reduce(
    (sum, question) => sum + Number(selectedRiskOption(questionnaire, question) || 0),
    0
  );
}

export function allRiskAnswered(questionnaire: RiskQuestionnaire): boolean {
  return riskQuestions.every((question) =>
    Boolean(selectedRiskOption(questionnaire, question))
  );
}

// "" until all 9 are answered; then conservative/moderate/aggressive by score.
// Result auto-reveals in the UI — no "view result" button (design spec).
export function computeRiskProfile(questionnaire: RiskQuestionnaire): string {
  if (!allRiskAnswered(questionnaire)) return "";
  const total = computeRiskScore(questionnaire);
  if (total <= 15) return "conservative";
  if (total <= 22) return "moderate";
  return "aggressive";
}
